import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import SelectCuenta from '../../components/SelectCuenta'
import { supabase } from '../../lib/supabase'

const fmt = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')
const ORIGEN_LABEL = {
  pagos:        { label: 'Cobro suscripción', color: '#16a34a', bg: '#d1fae5' },
  compras:      { label: 'Compra proveedor',  color: '#dc2626', bg: '#fee2e2' },
  gastos_fijos: { label: 'Gasto fijo mensual', color: '#92400e', bg: '#fef3c7' },
  cfe_emitido:  { label: 'CFE emitido',        color: '#3730a3', bg: '#e0e7ff' },
  manual:       { label: 'Manual',             color: '#475569', bg: '#f1f5f9' },
  ajuste:       { label: 'Ajuste',             color: '#7c3aed', bg: '#ede9fe' },
  extorno:      { label: '⤺ Extorno',          color: '#b45309', bg: '#fef3c7' },
}

export default function Asientos() {
  const [asientos, setAsientos] = useState([])
  const [lineasPorAsiento, setLineasPorAsiento] = useState({})
  const [loading, setLoading] = useState(true)
  const [filtroOrigen, setFiltroOrigen] = useState('')
  const [filtroMes, setFiltroMes] = useState(() => new Date().toISOString().slice(0, 7))
  const [filtroRango, setFiltroRango] = useState('todo')  // 'mes-actual' | 'ultimos-3' | 'ultimos-6' | 'anio' | 'todo' | 'mes-especifico'
  const [filtroEstado, setFiltroEstado] = useState('')  // '' | 'borrador' | 'confirmado' | 'anulado'
  const [busqueda, setBusqueda] = useState('')
  const [expandidos, setExpandidos] = useState(() => new Set())
  const [ejecutando, setEjecutando] = useState(false)
  const [editando, setEditando] = useState(null)   // asiento siendo editado (full obj con líneas)
  const [planCuentas, setPlanCuentas] = useState([])  // cache global de cuentas activas

  useEffect(() => { load() }, [filtroMes, filtroRango])
  useEffect(() => {
    // Cargar plan de cuentas Zeta desde cuentas_contables (368 cuentas oficiales).
    // Solo imputables (hojas) — los grupos no reciben asientos directos.
    supabase.from('cuentas_contables')
      .select('codigo, nombre, tipo, es_imputable')
      .eq('activa', true)
      .eq('es_imputable', true)
      .order('codigo')
      .then(({ data }) => setPlanCuentas(data || []))
  }, [])

  async function load() {
    setLoading(true)
    let desde = null
    let hasta = null
    const hoy = new Date()

    if (filtroRango === 'mes-actual') {
      const y = hoy.getFullYear(), m = hoy.getMonth() // 0-11
      desde = new Date(y, m, 1).toISOString().slice(0, 10)
      hasta = new Date(y, m + 1, 1).toISOString().slice(0, 10)
    } else if (filtroRango === 'ultimos-3') {
      desde = new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1).toISOString().slice(0, 10)
      hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1).toISOString().slice(0, 10)
    } else if (filtroRango === 'ultimos-6') {
      desde = new Date(hoy.getFullYear(), hoy.getMonth() - 5, 1).toISOString().slice(0, 10)
      hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1).toISOString().slice(0, 10)
    } else if (filtroRango === 'anio') {
      desde = `${hoy.getFullYear()}-01-01`
      hasta = `${hoy.getFullYear() + 1}-01-01`
    } else if (filtroRango === 'mes-especifico' && filtroMes) {
      const [year, month] = filtroMes.split('-').map(Number)
      desde = `${filtroMes}-01`
      hasta = new Date(year, month, 1).toISOString().slice(0, 10)
    }
    // filtroRango === 'todo' → sin desde/hasta, trae TODOS

    let q = supabase.from('asientos_contables').select('*').order('fecha', { ascending: false }).order('numero', { ascending: false }).limit(2000)
    if (desde && hasta) q = q.gte('fecha', desde).lt('fecha', hasta)
    const { data, error } = await q
    if (error) console.error(error)
    setAsientos(data || [])
    setLoading(false)
  }

  async function cargarLineas(asientoId) {
    if (lineasPorAsiento[asientoId]) return
    const { data } = await supabase.from('asientos_lineas')
      .select('*, cuenta:cuentas_contables!asientos_lineas_cuenta_codigo_fkey(nombre, tipo)')
      .eq('asiento_id', asientoId)
      .order('orden')
    setLineasPorAsiento(prev => ({ ...prev, [asientoId]: data || [] }))
  }

  function toggleExpand(id) {
    setExpandidos(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else { next.add(id); cargarLineas(id) }
      return next
    })
  }

  async function generarAsientosGastosFijos() {
    if (!confirm(`¿Generar asientos de gastos fijos para ${filtroMes}? Si ya existen, no se duplican.`)) return
    setEjecutando(true)
    try {
      const { data, error } = await supabase.rpc('crear_asientos_gastos_fijos_mes', {
        p_mes: filtroMes + '-01',
      })
      if (error) throw error
      alert(`Listo. Se generaron ${data} asientos.`)
      load()
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      setEjecutando(false)
    }
  }

  const filtrados = useMemo(() => {
    let f = asientos
    if (filtroOrigen) f = f.filter(a => a.origen_tabla === filtroOrigen)
    if (filtroEstado) f = f.filter(a => a.estado === filtroEstado)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(a =>
        (a.concepto || '').toLowerCase().includes(q) ||
        String(a.numero).includes(q)
      )
    }
    return f
  }, [asientos, filtroOrigen, filtroEstado, busqueda])

  const kpis = useMemo(() => {
    let totalDebe = 0
    let totalAsientos = filtrados.length
    let porOrigen = {}
    for (const a of filtrados) {
      totalDebe += Number(a.monto_total)
      porOrigen[a.origen_tabla] = (porOrigen[a.origen_tabla] || 0) + 1
    }
    return { totalAsientos, totalMovido: totalDebe, porOrigen }
  }, [filtrados])

  return (
    <AdminLayout>
      <div className="as-wrap">
        <div className="as-headline">
          <div>
            <h1>Asientos contables</h1>
            <p>Histórico de asientos generados automáticamente desde cobros, compras y gastos fijos.</p>
          </div>
          <div className="as-actions">
            <button className="as-btn-secondary" onClick={generarAsientosGastosFijos} disabled={ejecutando}>
              {ejecutando ? 'Generando…' : '⚙️ Generar gastos fijos del mes'}
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="as-kpis">
          <div className="as-kpi">
            <div className="as-kpi-l">Asientos del período</div>
            <div className="as-kpi-v">{kpis.totalAsientos}</div>
          </div>
          <div className="as-kpi">
            <div className="as-kpi-l">Total movido (DEBE)</div>
            <div className="as-kpi-v">{fmt(kpis.totalMovido)}</div>
          </div>
          <div className="as-kpi">
            <div className="as-kpi-l">Cobros</div>
            <div className="as-kpi-v" style={{ color: '#16a34a' }}>{kpis.porOrigen.pagos || 0}</div>
          </div>
          <div className="as-kpi">
            <div className="as-kpi-l">Compras</div>
            <div className="as-kpi-v" style={{ color: '#dc2626' }}>{kpis.porOrigen.compras || 0}</div>
          </div>
          <div className="as-kpi">
            <div className="as-kpi-l">Gastos fijos</div>
            <div className="as-kpi-v" style={{ color: '#92400e' }}>{kpis.porOrigen.gastos_fijos || 0}</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="as-toolbar">
          <label className="as-fil">
            <span>Rango</span>
            <select value={filtroRango} onChange={(e) => setFiltroRango(e.target.value)}>
              <option value="todo">📚 Todos (histórico completo)</option>
              <option value="mes-actual">Este mes</option>
              <option value="ultimos-3">Últimos 3 meses</option>
              <option value="ultimos-6">Últimos 6 meses</option>
              <option value="anio">Año actual</option>
              <option value="mes-especifico">Mes específico…</option>
            </select>
          </label>
          {filtroRango === 'mes-especifico' && (
            <label className="as-fil">
              <span>Mes</span>
              <input type="month" value={filtroMes} onChange={(e) => setFiltroMes(e.target.value)} />
            </label>
          )}
          <label className="as-fil">
            <span>Estado</span>
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
              <option value="">Todos</option>
              <option value="borrador">📝 Borradores</option>
              <option value="confirmado">✓ Confirmados</option>
              <option value="anulado">⛔ Anulados / Extornados</option>
            </select>
          </label>
          <label className="as-fil">
            <span>Origen</span>
            <select value={filtroOrigen} onChange={(e) => setFiltroOrigen(e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(ORIGEN_LABEL).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </label>
          <label className="as-fil">
            <span>Buscar</span>
            <input type="search" placeholder="Concepto o #…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </label>
          <div className="as-toolbar-info">{filtrados.length} asientos</div>
        </div>

        {/* Lista */}
        {loading ? (
          <div className="as-loading">Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div className="as-empty">No hay asientos en este período.</div>
        ) : (
          <div className="as-lista">
            {filtrados.map(a => {
              const origen = ORIGEN_LABEL[a.origen_tabla] || ORIGEN_LABEL.manual
              const expandido = expandidos.has(a.id)
              const lineas = lineasPorAsiento[a.id] || []
              return (
                <div className="as-asiento" key={a.id}>
                  <button className="as-asiento-head" onClick={() => toggleExpand(a.id)}>
                    <div className="as-asiento-num">#{a.numero}</div>
                    <div className="as-asiento-fecha">{a.fecha}</div>
                    <div className="as-asiento-concepto">{a.concepto}</div>
                    <span className="as-asiento-pill" style={{ background: origen.bg, color: origen.color }}>
                      {origen.label}
                    </span>
                    {/* Estado contable del asiento (compacto) */}
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap',
                      background: a.estado === 'confirmado' ? '#dcfce7' : a.estado === 'anulado' ? '#fee2e2' : '#fef3c7',
                      color: a.estado === 'confirmado' ? '#166534' : a.estado === 'anulado' ? '#991b1b' : '#92400e',
                    }} title={a.estado === 'borrador' ? 'Borrador: editable. Click "Confirmar" para hacerlo firme.' : a.estado === 'confirmado' ? 'Confirmado: parte del libro mayor. Para corregir, anulá y rehacé.' : 'Anulado'}>
                      {a.estado === 'borrador' ? '📝 Borr.' : a.estado === 'confirmado' ? '✓ OK' : '⛔ Anul.'}
                    </span>
                    <div className="as-asiento-monto">{fmt(a.monto_total)}</div>
                    <div className="as-asiento-zeta" title={a.zeta_id ? 'Sincronizado con Zeta (libro oficial)' : 'Pendiente: falta cargar a Zeta manualmente'}>
                      {a.zeta_id ? <span style={{ color: '#16a34a' }}>✓</span> : <span style={{ color: '#cbd5e1' }}>⏳</span>}
                    </div>
                    <div className="as-asiento-chevron">{expandido ? '▼' : '▶'}</div>
                  </button>
                  {expandido && (
                    <div className="as-lineas">
                      <table>
                        <thead>
                          <tr>
                            <th>Cuenta</th>
                            <th>Detalle</th>
                            <th style={{ textAlign: 'right' }}>Debe</th>
                            <th style={{ textAlign: 'right' }}>Haber</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineas.map(l => (
                            <tr key={l.id}>
                              <td>
                                <code>{l.cuenta_codigo}</code> {l.cuenta?.nombre || ''}
                              </td>
                              <td style={{ color: '#64748b', fontSize: 12 }}>{l.detalle || ''}</td>
                              <td style={{ textAlign: 'right', color: Number(l.debe) > 0 ? '#dc2626' : '#cbd5e1' }}>
                                {Number(l.debe) > 0 ? fmt(l.debe) : '—'}
                              </td>
                              <td style={{ textAlign: 'right', color: Number(l.haber) > 0 ? '#16a34a' : '#cbd5e1' }}>
                                {Number(l.haber) > 0 ? fmt(l.haber) : '—'}
                              </td>
                            </tr>
                          ))}
                          {lineas.length === 0 && (
                            <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: 14 }}>Cargando líneas…</td></tr>
                          )}
                        </tbody>
                      </table>
                      {/* Acciones sobre el asiento */}
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '8px 4px 0', flexWrap: 'wrap' }}>
                        {a.estado === 'borrador' && (
                          <>
                            <button
                              onClick={async () => {
                                if (!confirm(`¿Confirmar asiento #${a.numero}? Después de confirmar, queda firme en el libro mayor y NO se puede editar (solo anular y rehacer).`)) return
                                const { error } = await supabase.from('asientos_contables').update({ estado: 'confirmado', updated_at: new Date().toISOString() }).eq('id', a.id)
                                if (error) { alert('Error: ' + error.message); return }
                                load()
                              }}
                              style={{ padding: '5px 12px', background: '#dcfce7', border: '1px solid #86efac', color: '#166534', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✓ Confirmar</button>
                            <button
                              onClick={() => setEditando({ ...a, lineas: lineas.length > 0 ? lineas : [] })}
                              style={{ padding: '5px 12px', background: '#fff', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>✏️ Editar</button>
                            <button
                              onClick={async () => {
                                if (!confirm(`¿Eliminar asiento borrador #${a.numero}? Si vino de una compra, la compra vuelve a estado "aprobada" sin asiento, y podés re-aprobar con cuenta correcta.`)) return
                                // Si vino de compras, desvincular primero
                                if (a.origen_tabla === 'compras' && a.origen_id) {
                                  await supabase.from('compras').update({ asiento_id: null, estado: 'pendiente_revision' }).eq('id', a.origen_id)
                                }
                                await supabase.from('asientos_lineas').delete().eq('asiento_id', a.id)
                                const { error } = await supabase.from('asientos_contables').delete().eq('id', a.id)
                                if (error) { alert('Error: ' + error.message); return }
                                load()
                              }}
                              style={{ padding: '5px 12px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>🗑 Eliminar</button>
                          </>
                        )}
                        {a.estado === 'confirmado' && (
                          <button
                            onClick={async () => {
                              const motivo = prompt(`¿Anular asiento confirmado #${a.numero}? Indicá el motivo (queda registrado):`)
                              if (!motivo) return
                              const { error } = await supabase.from('asientos_contables').update({ estado: 'anulado', anulado_at: new Date().toISOString(), anulado_motivo: motivo, updated_at: new Date().toISOString() }).eq('id', a.id)
                              if (error) { alert('Error: ' + error.message); return }
                              load()
                            }}
                            style={{ padding: '5px 12px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>⛔ Anular</button>
                        )}
                        {a.estado !== 'anulado' && (
                          <button
                            title="Genera un contra-asiento (mismas cuentas, DEBE↔HABER invertidos), marca este como anulado y — si vino de una compra — libera la compra para re-aprobarla con las cuentas correctas."
                            onClick={async () => {
                              const motivo = prompt(`Extornar asiento #${a.numero} — ${a.concepto}?\n\nGenera un contra-asiento y marca este como anulado. Motivo:`, 'Reclasificación de cuentas contables')
                              if (!motivo) return
                              const { data, error } = await supabase.rpc('extornar_asiento', { p_asiento_id: a.id, p_motivo: motivo })
                              if (error) { alert('Error: ' + error.message); return }
                              load()
                            }}
                            style={{ padding: '5px 12px', background: '#fef3c7', border: '1px solid #fbbf24', color: '#92400e', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>⤺ Extornar</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Info */}
        <div className="as-info">
          <strong>📚 Cómo se generan los asientos automáticos:</strong>
          <ul>
            <li><b>Cobros</b>: cuando un pago pasa a estado <code>pagado</code> (trigger en tabla <code>pagos</code>). DEBE Banco/Handy + HABER Ingreso + HABER IVA débito 22%.</li>
            <li><b>Compras</b>: cuando una compra pasa a <code>aprobada</code> o <code>pagada</code> (trigger en tabla <code>compras</code>). DEBE Egreso + DEBE IVA crédito + HABER Proveedores.</li>
            <li><b>Gastos fijos</b>: día 1 de cada mes a las 02:00 AM (cron pg_cron). Idempotente — no duplica si ya se corrió. Botón "Generar gastos fijos" arriba lo dispara manualmente.</li>
            <li><b>Anticipo IRAE</b>: se trata como ACTIVO (cuenta <code>1.1.03.01</code>), no como gasto. NO baja base imponible IRAE.</li>
          </ul>
        </div>
      </div>

      {editando && (
        <ModalEditarAsiento
          asiento={editando}
          planCuentas={planCuentas}
          onClose={() => setEditando(null)}
          onGuardado={() => { setEditando(null); load(); setLineasPorAsiento({}) }}
        />
      )}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

/* ─────────── Modal: Editar líneas de asiento (solo borrador) ─────────── */
function ModalEditarAsiento({ asiento, planCuentas, onClose, onGuardado }) {
  const storageKey = `asiento-borrador-${asiento.id}`
  const [decidido, setDecidido] = useState(false)

  const [fecha, setFecha] = useState(asiento.fecha)
  const [concepto, setConcepto] = useState(asiento.concepto || '')
  const [lineas, setLineas] = useState(() =>
    (asiento.lineas || []).map(l => ({
      id: l.id, cuenta_codigo: l.cuenta_codigo, detalle: l.detalle || '',
      debe: Number(l.debe || 0), haber: Number(l.haber || 0),
    }))
  )
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)
  const [autoSaved, setAutoSaved] = useState(null)

  // Chequear borrador UNA vez al montar (dentro de useEffect, no en el body — sino se dispara en cada render)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const b = JSON.parse(raw)
        if (Date.now() - b.timestamp < 7 * 86400000 && Array.isArray(b.lineas)) {
          const restaurar = window.confirm(
            `Hay cambios sin guardar del ${new Date(b.timestamp).toLocaleString('es-UY')}.\n\n¿Restaurar el borrador? (Cancelar = descartar y arrancar de las líneas actuales)`
          )
          if (restaurar) {
            setFecha(b.fecha)
            setConcepto(b.concepto)
            setLineas(b.lineas)
          } else {
            localStorage.removeItem(storageKey)  // Descarto para no volver a preguntar
          }
        }
      }
    } catch {}
    setDecidido(true)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save debounce 300ms — SOLO después de decidir restaurar/descartar
  useEffect(() => {
    if (!decidido) return
    const t = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          fecha, concepto, lineas, timestamp: Date.now(),
        }))
        setAutoSaved(new Date())
      } catch {}
    }, 300)
    return () => clearTimeout(t)
  }, [fecha, concepto, lineas, storageKey, decidido])

  const totalDebe = lineas.reduce((s, l) => s + Number(l.debe || 0), 0)
  const totalHaber = lineas.reduce((s, l) => s + Number(l.haber || 0), 0)
  const balanceado = Math.abs(totalDebe - totalHaber) < 0.01

  function actualizarLinea(idx, field, val) {
    setLineas(l => l.map((line, i) => i === idx ? { ...line, [field]: val } : line))
  }
  function eliminarLinea(idx) {
    setLineas(l => l.filter((_, i) => i !== idx))
  }
  function agregarLinea() {
    setLineas(l => [...l, { cuenta_codigo: '', detalle: '', debe: 0, haber: 0 }])
  }

  async function guardar() {
    setError(null)
    if (!balanceado) { setError(`Asiento descuadrado: DEBE $${fmt(totalDebe)} ≠ HABER $${fmt(totalHaber)}. Diferencia: $${fmt(Math.abs(totalDebe - totalHaber))}`); return }
    if (lineas.length === 0) { setError('Tiene que haber al menos una línea'); return }
    for (const [i, l] of lineas.entries()) {
      if (!l.cuenta_codigo) { setError(`Línea ${i + 1}: falta cuenta contable`); return }
      if (Number(l.debe) === 0 && Number(l.haber) === 0) { setError(`Línea ${i + 1}: debe o haber tiene que ser > 0`); return }
      if (Number(l.debe) > 0 && Number(l.haber) > 0) { setError(`Línea ${i + 1}: no puede tener debe Y haber al mismo tiempo`); return }
    }
    setGuardando(true)
    try {
      // 1) Update header
      const { error: e1 } = await supabase.from('asientos_contables').update({
        fecha, concepto, monto_total: totalDebe, updated_at: new Date().toISOString(),
      }).eq('id', asiento.id)
      if (e1) throw e1
      // 2) Borrar líneas viejas y reinsertar (más simple que diff)
      const { error: e2 } = await supabase.from('asientos_lineas').delete().eq('asiento_id', asiento.id)
      if (e2) throw e2
      const { error: e3 } = await supabase.from('asientos_lineas').insert(
        lineas.map((l, i) => ({
          asiento_id: asiento.id,
          cuenta_codigo: l.cuenta_codigo,
          debe: Number(l.debe || 0),
          haber: Number(l.haber || 0),
          detalle: l.detalle || null,
          orden: i + 1,
        }))
      )
      if (e3) throw e3
      localStorage.removeItem(storageKey)  // Limpio el borrador — ya está persistido en BD
      onGuardado()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div onClick={onClose} style={modalBg}>
      <div onClick={e => e.stopPropagation()} style={modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, color: '#1a3461', fontSize: 18 }}>✏️ Editar asiento #{asiento.numero}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 22, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, marginBottom: 14 }}>
          <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            Fecha
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={inpModal} />
          </label>
          <label style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            Concepto
            <input type="text" value={concepto} onChange={e => setConcepto(e.target.value)} style={inpModal} />
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 13, color: '#1a3461' }}>Líneas ({lineas.length})</h3>
          <button onClick={agregarLinea} style={{ padding: '4px 10px', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>+ Agregar línea</button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Cuenta</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: 10, color: '#64748b', textTransform: 'uppercase' }}>Detalle</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: '#64748b', textTransform: 'uppercase', width: 100 }}>Debe</th>
              <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10, color: '#64748b', textTransform: 'uppercase', width: 100 }}>Haber</th>
              <th style={{ width: 28 }}></th>
            </tr>
          </thead>
          <tbody>
            {lineas.map((l, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '4px', minWidth: 240 }}>
                  <SelectCuenta
                    value={l.cuenta_codigo}
                    onChange={cod => actualizarLinea(i, 'cuenta_codigo', cod)}
                    cuentas={planCuentas}
                    size="sm"
                  />
                </td>
                <td style={{ padding: '4px' }}>
                  <input type="text" value={l.detalle} onChange={e => actualizarLinea(i, 'detalle', e.target.value)} style={{ ...inpModal, fontSize: 11, padding: '4px 6px' }} placeholder="Opcional" />
                </td>
                <td style={{ padding: '4px' }}>
                  <input type="number" step="0.01" min="0" value={l.debe || ''} onChange={e => { const v = Number(e.target.value) || 0; actualizarLinea(i, 'debe', v); if (v > 0) actualizarLinea(i, 'haber', 0) }}
                    style={{ ...inpModal, fontSize: 11, padding: '4px 6px', textAlign: 'right', color: l.debe > 0 ? '#dc2626' : '#1a3461', fontWeight: l.debe > 0 ? 700 : 400 }} />
                </td>
                <td style={{ padding: '4px' }}>
                  <input type="number" step="0.01" min="0" value={l.haber || ''} onChange={e => { const v = Number(e.target.value) || 0; actualizarLinea(i, 'haber', v); if (v > 0) actualizarLinea(i, 'debe', 0) }}
                    style={{ ...inpModal, fontSize: 11, padding: '4px 6px', textAlign: 'right', color: l.haber > 0 ? '#16a34a' : '#1a3461', fontWeight: l.haber > 0 ? 700 : 400 }} />
                </td>
                <td style={{ padding: '4px', textAlign: 'center' }}>
                  <button onClick={() => eliminarLinea(i)} style={{ background: 'transparent', border: 0, color: '#dc2626', cursor: 'pointer', fontSize: 14 }}>×</button>
                </td>
              </tr>
            ))}
            {lineas.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>Sin líneas. Agregá al menos una.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #1a3461', background: '#f8fafc', fontWeight: 800 }}>
              <td colSpan={2} style={{ padding: '6px 8px', textAlign: 'right' }}>Totales:</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#dc2626' }}>{fmt(totalDebe)}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#16a34a' }}>{fmt(totalHaber)}</td>
              <td></td>
            </tr>
            <tr>
              <td colSpan={5} style={{ padding: '6px 8px', textAlign: 'right', fontSize: 11, color: balanceado ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                {balanceado ? '✓ Asiento balanceado' : `✗ Descuadre: $${fmt(Math.abs(totalDebe - totalHaber))}`}
              </td>
            </tr>
          </tfoot>
        </table>

        {error && <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
          <div style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
            {autoSaved ? (
              <>
                <span style={{ color: '#16a34a' }}>💾 Auto-guardado local</span>
                <span style={{ color: '#94a3b8' }}>{autoSaved.toLocaleTimeString('es-UY')}</span>
                <span style={{ color: '#94a3b8' }}>· Si te vas de la pantalla, al volver te lo restauro.</span>
              </>
            ) : (
              <span style={{ color: '#94a3b8' }}>Los cambios se auto-guardan localmente mientras editás.</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={guardando} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cerrar</button>
            <button onClick={guardar} disabled={guardando || !balanceado} style={{ padding: '8px 18px', background: balanceado ? '#1a3461' : '#cbd5e1', color: '#fff', border: 0, borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: balanceado ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
              {guardando ? '⏳ Guardando…' : '💾 Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const modalBg = { position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto' }
const modalCard = { background: '#fff', borderRadius: 12, padding: 20, maxWidth: 720, width: '100%', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', marginTop: 30 }
const inpModal = { width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, color: '#1a3461', fontFamily: 'inherit', boxSizing: 'border-box', marginTop: 4 }

const CSS = `
.as-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.as-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 16px }
.as-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.as-headline p { font-size: 13px; color: #64748b; margin: 0 }
.as-actions { display: flex; gap: 8px; flex-wrap: wrap }
.as-btn-secondary { padding: 8px 14px; background: #1A3461; border: 0; color: #fff; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.as-btn-secondary:hover { background: #0f2147 }
.as-btn-secondary:disabled { opacity: .5; cursor: not-allowed }

.as-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; margin-bottom: 14px }
.as-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.as-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 700; letter-spacing: .04em }
.as-kpi-v { font-size: 22px; font-weight: 900; color: #1A3461 }

.as-toolbar { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-end; margin-bottom: 14px }
.as-fil { display: flex; flex-direction: column; gap: 4px }
.as-fil span { font-size: 11px; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
.as-fil input, .as-fil select { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit; color: #1A3461 }
.as-toolbar-info { margin-left: auto; font-size: 12px; color: #64748b }

.as-loading, .as-empty { padding: 40px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }

.as-lista { display: flex; flex-direction: column; gap: 8px }
.as-asiento { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; margin-bottom: 4px }
.as-asiento-head { display: grid; grid-template-columns: 50px 80px 1fr 130px 100px 90px 36px 20px; gap: 8px; align-items: center; padding: 6px 12px; width: 100%; border: 0; background: transparent; cursor: pointer; font-family: inherit; text-align: left; min-height: 36px }
.as-asiento-head:hover { background: #f8fafc }
.as-asiento-num { font-size: 11px; font-weight: 800; color: #64748b; font-family: ui-monospace, monospace }
.as-asiento-fecha { font-size: 11px; color: #64748b; white-space: nowrap }
.as-asiento-concepto { font-size: 13px; font-weight: 700; color: #1A3461; overflow: hidden; text-overflow: ellipsis; white-space: nowrap }
.as-asiento-pill { padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em; text-align: center; white-space: nowrap }
.as-asiento-monto { text-align: right; font-size: 13px; font-weight: 800; color: #1A3461; white-space: nowrap }
.as-asiento-zeta { font-size: 14px; text-align: center; line-height: 1; white-space: nowrap }
.as-asiento-chevron { color: #cbd5e1; font-size: 10px; text-align: right }

.as-lineas { background: #f8fafc; padding: 0 16px 12px; border-top: 1px solid #e2e8f0 }
.as-lineas table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 6px }
.as-lineas th { text-align: left; padding: 8px 6px; font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.as-lineas td { padding: 7px 6px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.as-lineas code { background: #e0e7ff; color: #3730a3; padding: 1px 6px; border-radius: 4px; font-size: 11.5px; font-weight: 700 }

.as-info { margin-top: 20px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.6 }
.as-info strong { font-size: 13px }
.as-info ul { margin: 8px 0 0; padding-left: 22px }
.as-info li { margin-bottom: 4px }
.as-info b { color: #1e40af }
.as-info code { background: #dbeafe; padding: 1px 6px; border-radius: 4px; font-size: 11.5px }

@media (max-width: 980px) {
  .as-asiento-head { grid-template-columns: 60px 1fr 130px 24px; gap: 8px }
  .as-asiento-fecha, .as-asiento-zeta { display: none }
  .as-asiento-pill { display: none }
}
`
